import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { claimAccess, createPackageCheckout, formatPrice, getViewerEmail, priceFor } from "./learnApi";

const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  "pk_live_51SDWk7GLCy9PVleCNU1Eow6f4yVZDhCTbLH0vNdUKbBqk05OsixJ2o17BfOwgXv5G69VsQe64z13tpB0nCtkwGrF00w3Ju6FYI";

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Unlock sheet for package-only content (Summit 2026 recordings).
 *   Buy tab    → pick a package → embedded Stripe Checkout, returns to `returnTo`.
 *                2026 attendees see the discounted attendee rate (the server
 *                decides the final rate from the checkout email).
 *   Claim tab  → speaker / buyer enters their email → set-password link.
 */
const PackageSheet = ({ open, onClose, packages = [], preferredSlug, returnTo = "/learn", initialTab = "buy", isAttendee = false }) => {
  const [tab, setTab] = useState(initialTab);
  const [slug, setSlug] = useState(preferredSlug || packages[0]?.slug || "");
  const [email, setEmail] = useState(getViewerEmail());
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [checkoutRate, setCheckoutRate] = useState("standard");
  const [claimState, setClaimState] = useState(null); // null | 'sent' | 'not_found' | 'throttled'

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setSlug(preferredSlug || packages[0]?.slug || "");
    setError("");
    setClientSecret("");
    setClaimState(null);
    setSubmitting(false);
  }, [open, initialTab, preferredSlug, packages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const selected = useMemo(() => packages.find((p) => p.slug === slug) || null, [packages, slug]);

  if (!open) return null;

  const startCheckout = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    if (!selected) { setError("Choose a package to continue."); return; }
    if (!EMAIL_REGEX.test(cleanEmail)) { setError("Enter the email you'll use to sign in."); return; }
    setSubmitting(true);
    try {
      const data = await createPackageCheckout({ packageSlug: selected.slug, email: cleanEmail, name: name.trim(), returnTo });
      setCheckoutRate(data.rate || "standard");
      setClientSecret(data.clientSecret);
    } catch (err) {
      setError(err?.data?.error === "not_configured"
        ? "Checkout isn't configured for this package yet — please try again soon."
        : "Something went wrong starting checkout. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(cleanEmail)) { setError("Enter the email you registered with."); return; }
    setSubmitting(true);
    try {
      const data = await claimAccess(cleanEmail);
      if (!data.entitled) setClaimState("not_found");
      else if (data.throttled) setClaimState("throttled");
      else setClaimState("sent");
    } catch (_) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="learn-sheet" role="dialog" aria-modal="true" aria-label="Unlock Summit recordings" onClick={onClose}>
      <div className="learn-sheet__inner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="learn-sheet__close" aria-label="Close" onClick={onClose}>×</button>

        <div className="learn-eyebrow">Hospitality Summit 2026</div>
        <h2 className="learn-sheet__title">Unlock the recordings</h2>

        {!clientSecret && (
          <div className="learn-tabs learn-tabs--sheet" role="tablist">
            {[["buy", "Buy a package"], ["claim", "I already have access"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={cn("learn-tab", { "learn-tab--active": tab === key })}
                onClick={() => { setTab(key); setError(""); setClaimState(null); }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {clientSecret ? (
          <div className="learn-sheet__checkout">
            <div className="learn-sheet__summary">
              <span>
                <strong>{selected?.title}</strong> · {formatPrice(priceFor(selected, checkoutRate === "attendee"))}
                {checkoutRate === "attendee" ? " (2026 attendee rate)" : ""} · {email.trim().toLowerCase()}
              </span>
              <button type="button" className="learn-sheet__edit" onClick={() => setClientSecret("")}>Edit</button>
            </div>
            <div className="learn-sheet__frame">
              <EmbeddedCheckoutProvider key={clientSecret} stripe={getStripe()} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        ) : tab === "buy" ? (
          <form className="learn-sheet__form" onSubmit={startCheckout} noValidate>
            {isAttendee && (
              <div className="learn-field-success">
                You were at the 2026 Summit — the attendee rate is applied for {getViewerEmail()}.
              </div>
            )}
            <div className="learn-packages" role="radiogroup" aria-label="Package">
              {packages.map((p) => (
                <label key={p.slug} className={cn("learn-package", { "learn-package--selected": p.slug === slug })}>
                  <input type="radio" name="package" value={p.slug} checked={p.slug === slug} onChange={() => setSlug(p.slug)} />
                  <span className="learn-package__body">
                    <span className="learn-package__title">{p.title.replace(/^Summit 2026\s*/, "")}</span>
                    {p.subtitle && <span className="learn-package__sub">{p.subtitle}</span>}
                  </span>
                  <span className="learn-package__price">
                    {isAttendee && p.attendee_price_cents != null ? (
                      <>
                        <s className="learn-price-was">{formatPrice(p.price_cents)}</s>
                        {formatPrice(p.attendee_price_cents)}
                      </>
                    ) : formatPrice(p.price_cents)}
                  </span>
                </label>
              ))}
            </div>
            <label className="learn-field">
              <span>Email — this is how you'll sign in</span>
              <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </label>
            <label className="learn-field">
              <span>Name <em>(optional)</em></span>
              <input type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            </label>
            {error && <div className="learn-field-error">{error}</div>}
            <button type="submit" className="learn-btn learn-btn--primary learn-sheet__submit" disabled={submitting || !selected}>
              {submitting ? "Preparing secure checkout…" : `Continue to payment — ${selected ? formatPrice(priceFor(selected, isAttendee)) : ""}`}
            </button>
            <p className="learn-sheet__fine">
              One-time purchase. Stream anytime inside Postaway Academy — recordings can't be downloaded or shared.
              {!isAttendee && " Attended the 2026 Summit? Use the email you registered with and the attendee rate ($100 per track, $175 for both) applies automatically."}
              {" "}Secure checkout by Stripe.
            </p>
          </form>
        ) : (
          <form className="learn-sheet__form" onSubmit={submitClaim} noValidate>
            <p className="learn-sheet__text">
              Spoke at the 2026 Summit, or already bought a package? Enter the email we have for you and we'll
              send a link to set your Postaway password. (Attendees: your discount is applied at checkout on the
              “Buy a package” tab — no claim needed.)
            </p>
            <label className="learn-field">
              <span>Email you registered with</span>
              <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => { setEmail(e.target.value); setClaimState(null); }} placeholder="you@example.com" />
            </label>
            {error && <div className="learn-field-error">{error}</div>}
            {claimState === "sent" && (
              <div className="learn-field-success">Check your inbox — your access link is on its way. It expires in 24 hours.</div>
            )}
            {claimState === "throttled" && (
              <div className="learn-field-success">We've already sent a link to that address recently — check your inbox (and spam).</div>
            )}
            {claimState === "not_found" && (
              <div className="learn-field-error">
                We don't have that email down for free access. Speakers: try the address Jamie has for you, or email
                {" "}<a href="mailto:jamie@postaway.com">jamie@postaway.com</a> and we'll sort it out.
              </div>
            )}
            <button type="submit" className="learn-btn learn-btn--primary learn-sheet__submit" disabled={submitting || claimState === "sent"}>
              {submitting ? "Checking…" : "Send my access link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PackageSheet;
